import { createContext, useContext, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react'
import { itemsForDataset, type Dataset, type DatasetItem } from 'app/components/ops-datasets/mockData'
import { loadAllDatasets } from 'app/components/ops-datasets/localStore'
import { logError } from 'lib/logging'
import { localPeopleExecutor, localPipelineExecutor, mockExecutor, type Executor } from './executor'
import { runEval, type RunProgressCount } from './runner'
import { loadStoredRuns, storeRun } from './runStore'
import { buildSeedRuns } from './mockData'
import { runLabels } from './runMeta'
import { EXECUTION_STAGES, type ExecutionMode, type Model, type Run, type RunConfig, type VersionEvent } from './types'
import { VERSION_EVENTS } from './mockData'

const EXECUTORS: Record<ExecutionMode, Executor> = {
  simulated: mockExecutor,
  'local-people': localPeopleExecutor,
  'local-pipeline': localPipelineExecutor,
}

/**
 * Session-wide prototype state for Ops → Evals. Seeds historical runs on mount (from
 * the golden dataset via the deterministic mock executor) and runs new evals through
 * the same client-side runner, with the executor the run's `execution` mode selects.
 * Real-model runs persist in this browser (runStore.ts); this provider is the single seam to
 * swap for real GraphQL + a server-side batch runner later (plan §7–§8).
 */
export type OpsEvalsContextValue = {
  runs: Run[]
  datasets: Dataset[]
  versionEvents: VersionEvent[]
  seeding: boolean
  running: boolean
  /** The run in flight: what it compares and how many calls have finished. */
  progress: RunProgress | null
  /** Why the last run failed, until the next run starts or it is dismissed. */
  runError: string | null
  dismissRunError: () => void
  /** Items in a dataset, resolved by tag membership (for pre-run estimates). */
  itemsFor: (datasetId: string) => DatasetItem[]
  /** Run an eval; prepends the result and returns its id (or null on failure). */
  createRun: (config: RunConfig) => Promise<string | null>
}

export type RunProgress = RunProgressCount & { title: string; execution: ExecutionMode; models: Model[] }

const OpsEvalsContext = createContext<OpsEvalsContextValue | null>(null)

/** Mount keyed by `workspaceId`, so a workspace switch reloads that workspace's datasets and runs. */
export function OpsEvalsProvider({
  workspaceId,
  children,
}: {
  workspaceId: string | undefined
  children: ReactNode
}): ReactElement {
  const [runs, setRuns] = useState<Run[]>([])
  const [seeding, setSeeding] = useState(true)
  const [progress, setProgress] = useState<RunProgress | null>(null)
  const [runError, setRunError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    buildSeedRuns()
      .then((seeded) => {
        // Real runs saved in this browser, newest first, then the simulated seed history.
        if (active) setRuns([...loadStoredRuns(workspaceId), ...seeded])
      })
      .catch((error) => logError(`Failed to seed eval runs`, error))
      .finally(() => {
        if (active) setSeeding(false)
      })
    return () => {
      active = false
    }
  }, [workspaceId])

  const value = useMemo<OpsEvalsContextValue>(() => {
    // Read fresh each time: Ops → Datasets saves imported calls to this browser.
    const { datasets, items } = loadAllDatasets(workspaceId)
    const itemsFor = (datasetId: string) => {
      const dataset = datasets.find((entry) => entry.id === datasetId)
      return dataset ? itemsForDataset(dataset, items) : []
    }

    const createRun = async (config: RunConfig) => {
      const dataset = datasets.find((entry) => entry.id === config.datasetId)
      if (!dataset) return null
      const execution = config.execution ?? `simulated`
      const items = itemsFor(dataset.id)
      const about = {
        title: runLabels({ ...config, stages: EXECUTION_STAGES[execution] }).title,
        execution,
        models: config.models,
      }
      setProgress({ ...about, done: 0, total: items.length * config.models.length, model: config.models[0] })
      setRunError(null)
      try {
        const run = await runEval({
          config,
          datasetName: dataset.name,
          items,
          executor: EXECUTORS[execution],
          onProgress: (count) => setProgress({ ...about, ...count }),
        })
        storeRun(workspaceId, run)
        setRuns((prev) => [run, ...prev])
        return run.id
      } catch (error) {
        logError(`Eval run failed`, error)
        setRunError(error instanceof Error ? error.message : String(error))
        return null
      } finally {
        setProgress(null)
      }
    }

    return {
      runs,
      datasets,
      versionEvents: VERSION_EVENTS,
      seeding,
      running: progress !== null,
      progress,
      runError,
      dismissRunError: () => setRunError(null),
      itemsFor,
      createRun,
    }
  }, [workspaceId, runs, seeding, progress, runError])

  return <OpsEvalsContext.Provider value={value}>{children}</OpsEvalsContext.Provider>
}

export function useOpsEvalsData(): OpsEvalsContextValue {
  const value = useContext(OpsEvalsContext)
  if (!value) {
    throw new Error(`useOpsEvalsData must be used within an OpsEvalsProvider`)
  }
  return value
}
